import asyncRetry from 'async-retry';
import { makeRequest } from './widget-facade';
import AmplitudeUtils from './amplitude-utils';
import { API_REQUEST_RETRY_MIN_TIMEOUT } from '../constants';

/**
 * Check if error is retryable (5xx server errors or no response)
 * Okta's invokeApiMethod returns errors with status in different places:
 * - error.xhr.status (wrapped errors)
 * - error.status (direct errors)
 * - error.responseJSON.status (some error formats)
 */
export function isRetryableError(error: any): boolean {
  if (!error) {
    return true; // No response - retry
  }

  const status = error?.xhr?.status || error?.status || error?.responseJSON?.status;
  // If status exists and is 5xx, it's retryable
  // If status doesn't exist, assume retryable (network error)
  if (status === undefined) {
    return true;
  }
  return status >= 500 && status <= 599;
}

/**
 * Generic retry wrapper for POST API calls made via makeRequest.
 * Retries up to 2 times on 5xx errors (3 total attempts).
 */
const MAX_RETRIES = 2;

export async function makeRequestWithRetry({
  url,
  args,
  retryEventName,
  metadata,
}: {
  url: string;
  args: any;
  retryEventName: string;
  metadata?: Record<string, any>;
}): Promise<any> {
  return asyncRetry(
    async (bail, attemptNumber) => {
      try {
        return await makeRequest({
          url,
          method: 'POST',
          args,
          headers: {
            'Content-Type': 'application/json',
          },
        });
      }
      catch (error: any) {
        const status = error?.xhr?.status || error?.status || error?.responseJSON?.status;
        const retryable = isRetryableError(error);
        const willRetry = retryable && attemptNumber <= MAX_RETRIES;

        // Log retry attempt (skip the final failed attempt, since no retry follows it)
        if (willRetry) {
          AmplitudeUtils.logCustomEvent(retryEventName, {
            ...metadata,
            attemptNumber,
            status,
          });
        }

        // If it's a retryable error (5xx), throw to trigger retry
        if (retryable) {
          throw error;
        }
        // Otherwise, bail out (don't retry)
        bail(error);
        return; // TypeScript needs this
      }
    },
    { retries: MAX_RETRIES, factor: 1, minTimeout: API_REQUEST_RETRY_MIN_TIMEOUT }
  );
}
